/**
 * spiceScheduleMove: the pure decision behind dragging a visit on the
 * SPICE week grid (src/components/team/SpiceWeekTab.tsx).
 *
 * The grid shows each session of a client's plan on the row of the
 * therapist who makes it: a visit dropped on another cell is a change to
 * that ONE session, its day on its own row and, on another therapist's
 * row, its therapist. No decision changes the client's own therapist.
 * Given the dragged entry, the cell it left, the cell it landed on, the
 * client's plan as a PUT body and the client's own therapist
 * (leadRosterId), planSpiceMove decides what the grid writes:
 *
 *   same-cell          nothing; the visit was put back where it was
 *   weekday-only       the plan PUT with the dragged session on the new
 *                      weekday, its time, kind, frequency, duration and
 *                      therapist kept, every other session and field
 *                      untouched; a monthly session takes the week of
 *                      the month of the date it was dropped on
 *                      (weekOfMonthForDrop), so it lands on that day
 *   session-therapist  the plan PUT with the dragged session's
 *                      therapist_roster_id set to the row's therapist
 *                      (null when the row is the client's own
 *                      therapist's, so the session follows the lead if
 *                      the office changes it later), its weekday changed
 *                      as above if it moved; every other session and
 *                      every plan field, the cover included, stay as
 *                      they are. A lead session of a covered plan dropped
 *                      on the cover's row becomes the cover's own
 *                      session, and the plan stays covered
 *   refused            with a reason: a cover entry (the session belongs
 *                      to its owner's row and the grid never lifts a
 *                      cover's copy of it; this is the gate behind that;
 *                      every other entry is its row's own session and
 *                      moves), a row that is not a SPICE therapist, a
 *                      session no longer on the plan (changed since the
 *                      grid loaded), or a drop where the moved session
 *                      can fall on the same date and time as another of
 *                      the client's sessions (spiceSessionsClash),
 *                      whoever makes it
 *
 * A session is its weekday, time and week of the month: the lookup, the
 * collision check and the drag id all name the week, so the third-Tuesday
 * visit moves the third Tuesday, never the first. The body handed in is
 * never mutated: it is what Undo puts back.
 *
 * Kept free of React, fetch and drag types, the workspaceStageMove
 * precedent, so every branch is unit-tested independent of the gesture.
 * The server still validates the plan it is handed.
 *
 * Also here, for the keyboard drag: nextSpiceGridCell, one arrow press
 * as a step to the neighbouring cell (left and right along the week,
 * up and down between therapist rows), and the drag and drop ids the
 * grid and its tests share.
 */

import type {
  SpiceScheduleSessionWire,
  SpiceSessionFrequency,
  SpiceVisitPlanInput,
  SpiceWeekOfMonth,
} from './spiceVisitPlansApi';
import { weekOfMonthForDrop } from './spiceNextSession';
import { addDays } from './capacity';

/** What the decision reads off a grid entry. */
export interface SpiceMoveEntry {
  weekday: number;
  start_time: string;
  week_of_month: SpiceWeekOfMonth | null;
  is_cover: boolean;
}

export interface SpiceMoveArgs {
  entry: SpiceMoveEntry;
  /** The row the entry was lifted from. */
  fromRosterId: string;
  /** The row and weekday it was dropped on. */
  toRosterId: string;
  toWeekday: number;
  /** The client's plan as it stands, as a PUT body (spicePlanToInput). */
  plan: SpiceVisitPlanInput;
  /** Every row of the grid: the SPICE therapists a client can sit with. */
  therapistRosterIds: ReadonlySet<string>;
  /** The Monday of the week in view: a monthly session's new week of the
   *  month comes from the date it was dropped on in this week. */
  weekStart: string;
  /** The client's own SPICE therapist (the plan GET's top-level
   *  therapist_roster_id): a drop on her row sends the session's
   *  therapist as null. */
  leadRosterId?: string | null;
}

/**
 * Whether two sessions can fall on the same date at the same time: the
 * same weekday and time, and either one is weekly or every two weeks, or
 * both are monthly in the same week, or one is the fourth and the other
 * the last (the same day in a month with four of that weekday). The
 * server's validator holds the same rule.
 */
export function spiceSessionsClash(
  a: { weekday: number; start_time: string; frequency: SpiceSessionFrequency; week_of_month: SpiceWeekOfMonth | null },
  b: { weekday: number; start_time: string; frequency: SpiceSessionFrequency; week_of_month: SpiceWeekOfMonth | null },
): boolean {
  if (a.weekday !== b.weekday || a.start_time !== b.start_time) return false;
  if (a.frequency !== 'monthly' || b.frequency !== 'monthly') return true;
  if (a.week_of_month === b.week_of_month) return true;
  const pair = new Set([a.week_of_month, b.week_of_month]);
  return pair.has('fourth') && pair.has('last');
}

export type SpiceMoveRefusal = 'cover' | 'not-a-therapist' | 'slot-not-on-plan' | 'slots-collide';

export type SpiceMoveDecision =
  | { kind: 'same-cell' }
  | { kind: 'weekday-only'; input: SpiceVisitPlanInput }
  | { kind: 'session-therapist'; input: SpiceVisitPlanInput }
  | { kind: 'refused'; reason: SpiceMoveRefusal };

export function planSpiceMove({
  entry,
  fromRosterId,
  toRosterId,
  toWeekday,
  plan,
  therapistRosterIds,
  weekStart,
  leadRosterId = null,
}: SpiceMoveArgs): SpiceMoveDecision {
  const rowChanged = toRosterId !== fromRosterId;
  const weekdayChanged = toWeekday !== entry.weekday;
  if (!rowChanged && !weekdayChanged) return { kind: 'same-cell' };
  if (entry.is_cover) return { kind: 'refused', reason: 'cover' };
  if (!therapistRosterIds.has(toRosterId)) return { kind: 'refused', reason: 'not-a-therapist' };

  const index = plan.sessions.findIndex(
    (s) =>
      s.weekday === entry.weekday &&
      s.start_time === entry.start_time &&
      (s.week_of_month ?? null) === (entry.week_of_month ?? null),
  );
  if (index === -1) return { kind: 'refused', reason: 'slot-not-on-plan' };

  const movedDay = (s: SpiceScheduleSessionWire): SpiceScheduleSessionWire => {
    if (!weekdayChanged) return { ...s };
    return s.frequency === 'monthly'
      ? { ...s, weekday: toWeekday, week_of_month: weekOfMonthForDrop(addDays(weekStart, toWeekday - 1), s.week_of_month) }
      : { ...s, weekday: toWeekday };
  };
  // On another therapist's row the session becomes hers; on the client's
  // own therapist's row it becomes the client's own again (null).
  const movedTo = (s: SpiceScheduleSessionWire): SpiceScheduleSessionWire =>
    rowChanged ? { ...movedDay(s), therapist_roster_id: toRosterId === leadRosterId ? null : toRosterId } : movedDay(s);
  const sessions = plan.sessions.map((s, i) => (i === index ? movedTo(s) : { ...s }));
  const moved = sessions[index]!;
  const collides = sessions.some((s, i) => i !== index && spiceSessionsClash(s, moved));
  if (collides) return { kind: 'refused', reason: 'slots-collide' };

  const input: SpiceVisitPlanInput = { ...plan, sessions };
  return rowChanged ? { kind: 'session-therapist', input } : { kind: 'weekday-only', input };
}

/** A refusal as the line the grid shows, naming the client. */
export function spiceMoveRefusalLine(reason: SpiceMoveRefusal, clientName: string): string {
  switch (reason) {
    case 'cover':
      return `${clientName}'s visit didn't move, since it can only be dragged from their own therapist's row. Drag it from there, or click the visit to change it.`;
    case 'not-a-therapist':
      return `That row isn't a SPICE therapist, so ${clientName} didn't move. Drop the visit on a therapist's row.`;
    case 'slots-collide':
      return `${clientName} already has a session at that day and time. Drop the visit somewhere else.`;
    case 'slot-not-on-plan':
      return `${clientName}'s schedule changed while you had it open, so the week has been reloaded. Move the visit again.`;
  }
}

/** A cell of the grid: one therapist row, one weekday column. */
export interface SpiceGridCell {
  rosterId: string;
  weekday: number;
}

/** The draggable id of an entry: one per client visit on the grid, the
 *  week of the month included, so two monthly sessions at one day and
 *  time are two ids. */
export function spiceEntryDragId(entry: {
  client_id: string;
  weekday: number;
  start_time: string;
  week_of_month: SpiceWeekOfMonth | null;
}): string {
  return `${entry.client_id}|${entry.weekday}|${entry.start_time}|${entry.week_of_month ?? '-'}`;
}

/** The droppable id of a cell. */
export function spiceCellDropId(cell: SpiceGridCell): string {
  return `${cell.rosterId}|${cell.weekday}`;
}

/**
 * One arrow press while a visit is lifted by keyboard: the neighbouring
 * cell, or null at an edge of the grid (the visit stays where it is).
 * `rosterIds` is the grid's rows in display order.
 */
export function nextSpiceGridCell(code: string, from: SpiceGridCell, rosterIds: readonly string[]): SpiceGridCell | null {
  const rowIndex = rosterIds.indexOf(from.rosterId);
  if (rowIndex === -1) return null;
  switch (code) {
    case 'ArrowLeft':
      return from.weekday > 1 ? { rosterId: from.rosterId, weekday: from.weekday - 1 } : null;
    case 'ArrowRight':
      return from.weekday < 5 ? { rosterId: from.rosterId, weekday: from.weekday + 1 } : null;
    case 'ArrowUp':
      return rowIndex > 0 ? { rosterId: rosterIds[rowIndex - 1]!, weekday: from.weekday } : null;
    case 'ArrowDown':
      return rowIndex < rosterIds.length - 1 ? { rosterId: rosterIds[rowIndex + 1]!, weekday: from.weekday } : null;
    default:
      return null;
  }
}
